'use client'

import { subscribe } from '@/src/app/actions/subscribe'
import { Button } from '@/src/components/ui/button'
import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'

// Esquema de validación con Zod
const newsletterFormSchema = z.object({
  email: z.string().email('Ingresa un email válido'),
})

// Tipo derivado del esquema
type NewsletterFormValues = z.infer<typeof newsletterFormSchema>

function Newsletter() {
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(false)
  const [showSuccessMessage, setShowSuccessMessage] = useState(false)

  // Inicializar el formulario con react-hook-form y zod
  const form = useForm<NewsletterFormValues>({
    resolver: zodResolver(newsletterFormSchema),
    defaultValues: {
      email: '',
    },
    mode: 'onChange', // Validar al cambiar para mostrar errores en tiempo real
  })

  // Activar la validación en tiempo real
  form.watch()

  const handleSubmit = async (data: NewsletterFormValues) => {
    setIsSubmitDisabled(true)

    try {
      // Crear FormData para la server action
      const formData = new FormData()
      formData.append('email', data.email)

      // Llamar a la server action
      const result = await subscribe(formData)

      if (!result.ok) {
        throw new Error(result.error || 'Error al procesar la suscripción')
      }

      // Mostrar mensaje de éxito
      setShowSuccessMessage(true)
      toast.success('¡Suscripción exitosa!', {
        description: 'Gracias por suscribirte. Pronto recibirás nuestras novedades.',
      })

      // Mantener el botón deshabilitado durante 5 segundos
      setTimeout(() => {
        setIsSubmitDisabled(false)
        setShowSuccessMessage(false)
        form.reset() // Limpiar el formulario después de 5 segundos
      }, 5000)
    }
    catch (err: any) {
      toast.error('Error', {
        description: 'message' in err ? err.message as string : 'Error al procesar la suscripción',
      })
      setIsSubmitDisabled(false)
    }
  }

  return (
    <section className="py-16 bg-srperrito-cream">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-srperrito-dark mb-4 font-serif">
            Recibe nuestras promociones y novedades
          </h2>
          <p className="text-lg text-srperrito-dark/80 mb-8">
            Suscríbete a nuestro boletín y sé el primero en enterarte de nuestras promociones especiales,
            nuevos productos y eventos.
          </p>

          {showSuccessMessage && (
            <div className="bg-green-100 text-green-700 p-4 rounded-md mb-6">
              ¡Gracias por suscribirte! Pronto recibirás nuestras novedades.
            </div>
          )}

          <form onSubmit={form.handleSubmit(handleSubmit)} className="flex flex-col sm:flex-row gap-4 justify-center">
            <div className="w-full sm:w-auto">
              <input
                type="email"
                {...form.register('email')}
                placeholder="Tu correo electrónico"
                className={`px-4 py-3 rounded-md focus:outline-none focus:ring-2 focus:ring-srperrito-dark border ${
                  form.formState.errors.email ? 'border-red-500' : 'border-gray-300'
                } min-w-[300px] w-full`}
              />
              {form.formState.errors.email && (
                <p className="mt-1 text-sm text-red-600 text-left">
                  {form.formState.errors.email.message}
                </p>
              )}
            </div>
            <Button
              type="submit"
              variant="primary"
              disabled={form.formState.isSubmitting || isSubmitDisabled}
              className="py-3 h-[50px]"
            >
              {form.formState.isSubmitting ? 'Suscribiendo...' : 'Suscribirme'}
            </Button>
          </form>

          <p className="mt-4 text-sm text-srperrito-dark/70">
            No compartimos tu información con terceros. Puedes darte de baja en cualquier momento.
          </p>
        </div>
      </div>
    </section>
  )
}

export default Newsletter
